import React, { FC, useRef, useState, useEffect } from 'react';
import { Button, Typography, Box, IconButton } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { styled } from '@mui/material/styles';
import DeleteIcon from '@mui/icons-material/Close';

interface FileInputProps {
  name: string;
  label: string;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  required?: boolean;
}

const HiddenInput = styled('input')({
  display: 'none',
});

const FileDisplay = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(1),
  padding: theme.spacing(1),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.paper,
  minHeight: '40px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

// Configurazione sicurezza
const ALLOWED_FILE_TYPES = [
  'image/jpeg',
  'image/jpg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/plain',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

// Funzione per validare il file
const validateFile = (file: File): string | null => {
  // Controlla dimensione
  if (file.size > MAX_FILE_SIZE) {
    return `File too large. Maximum size: ${Math.round(MAX_FILE_SIZE / 1024 / 1024)}MB`;
  }

  // Controlla tipo
  if (!ALLOWED_FILE_TYPES.includes(file.type)) {
    return `File type not allowed: ${file.type}`;
  }

  // Controlla estensione per sicurezza aggiuntiva
  const fileName = file.name.toLowerCase();
  const allowedExtensions = [
    '.jpg',
    '.jpeg',
    '.png',
    '.gif',
    '.webp',
    '.pdf',
    '.txt',
    '.doc',
    '.docx',
  ];
  const hasValidExtension = allowedExtensions.some((ext) =>
    fileName.endsWith(ext),
  );

  if (!hasValidExtension) {
    return 'File extension not allowed';
  }

  return null;
};

export const FileInput: FC<FileInputProps> = ({
  name,
  label,
  accept = '*/*',
  multiple = false,
  disabled = false,
  required = false,
}) => {
  const { control, setValue, setError, clearErrors } = useFormContext();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);
  const [fileNames, setFileNames] = useState<string[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  useEffect(() => {
    return () => {
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  const handleDelete = () => {
    previewUrls.forEach((url) => URL.revokeObjectURL(url));
    setPreviewUrls([]);
    setFileNames([]);
    setValidationError(null);
    clearErrors(name);
    setValue(name, multiple ? [] : null);
  };

  const handleFileChange = (
    files: FileList | null,
    onChange: (value: any) => void,
  ) => {
    if (!files || files.length === 0) {
      setFileNames([]);
      previewUrls.forEach((url) => URL.revokeObjectURL(url));
      setPreviewUrls([]);
      setValidationError(null);
      clearErrors(name);
      return;
    }

    const fileList = multiple ? Array.from(files) : [files[0]];
    let hasError = false;

    // Validazione di tutti i file
    for (const file of fileList) {
      const error = validateFile(file);
      if (error) {
        setValidationError(error);
        setError(name, { type: 'manual', message: error });
        hasError = true;
        break;
      }
    }

    if (!hasError) {
      setValidationError(null);
      clearErrors(name);
      onChange(multiple ? fileList : fileList[0]);
      setFileNames(fileList.map((f) => f.name));

      // Pulizia preview precedenti
      previewUrls.forEach((url) => URL.revokeObjectURL(url));

      // Crea preview solo per immagini
      const newPreviewUrls = fileList
        .filter((f) => f && f.type.startsWith('image/'))
        .map((f) => URL.createObjectURL(f));
      setPreviewUrls(newPreviewUrls);
    }
  };

  return (
    <Controller
      control={control}
      name={name}
      rules={required ? { required: 'Required field' } : undefined}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <Box>
          <Button
            variant="outlined"
            component="label"
            disabled={disabled}
            sx={{ mb: 1 }}
          >
            {label}
            <HiddenInput
              ref={fileInputRef}
              type="file"
              accept={accept}
              multiple={multiple}
              onChange={(e) => {
                handleFileChange(e.target.files, onChange);
                e.target.value = '';
              }}
            />
          </Button>

          {/* Messaggio di errore di validazione */}
          {(validationError || error) && (
            <Typography
              variant="caption"
              color="error"
              sx={{ display: 'block', mb: 1 }}
            >
              {validationError || error?.message}
            </Typography>
          )}

          {/* Informazioni sui tipi di file consentiti */}
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 1 }}
          >
            Allowed types: JPG, PNG, GIF, WEBP, PDF, TXT, DOC, DOCX (max 5MB)
          </Typography>

          {(value || previewUrls.length > 0) && (
            <FileDisplay>
              <Box display="flex" alignItems="center" flex="1">
                {previewUrls.length > 0 && (
                  <Box display="flex" gap={1} mr={2}>
                    {previewUrls.map((url, idx) => (
                      <img
                        key={url}
                        src={url}
                        alt={`attachment preview ${idx + 1}`}
                        style={{
                          maxWidth: 64,
                          maxHeight: 64,
                          borderRadius: 6,
                          border: '1px solid #eee',
                        }}
                      />
                    ))}
                  </Box>
                )}
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ wordBreak: 'break-word' }}
                >
                  {multiple && Array.isArray(value)
                    ? fileNames.length > 0
                      ? `${fileNames.length} files selected: ${fileNames.join(', ')}`
                      : 'No file selected'
                    : value && value.name
                      ? `Selected file: ${value.name}`
                      : 'No file selected'}
                </Typography>
              </Box>

              <IconButton onClick={handleDelete} color="error" size="small">
                <DeleteIcon />
              </IconButton>
            </FileDisplay>
          )}
        </Box>
      )}
    />
  );
};
